import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { setWorkingArticleNoteRelationAsync } from "./workingArticleNoteRelationSlice"
import axios from "axios"
import { fetchUniqueCounts } from "./workspaceNoteSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

// breadcrumb thunk should load in data when a tab is open
export const fetchArticleBreadcrumbs = createAsyncThunk(
	"breadcrumb/fetchArticleBreadcrumbs",
	async ({article, note, workspaceId}, { rejectWithValue, dispatch}) => {
		if (!workspaceId) {
			console.error("Failed to fetch breadcrumbs: invalid workspaceId")
			return rejectWithValue("Breadcrumbs failed to load. Invalid Workspace ID")
		}
		try {
			const {data} = await axios.get(`/api/workspace/${workspaceId}/article/${article.id}/note`)
			dispatch(setWorkingArticleNoteRelationAsync(workspaceId, article.id))
			// fetch unique count if note is passed in deletion or attachment
			if (note) {
				dispatch(fetchUniqueCounts({id: note.id, workspaceId}))
			}
			return {
				workspaceId,
				article,
				breadcrumbs: data
			}
		} catch (error) {
			console.error("Failed to fetch article breadcrumbs: ", error)
			return rejectWithValue(error?.response?.message || `Failed to load breadcrumbs for article ${article.id}`)
		}

	}
)

// NOTE: this is THE attachment thunk. Although articleDragSlice.setOnDrop gets invoked first, it does not make a request to the backend.
// even though this thunk is in the breadcrumb slice, this is the only way to trigger "NoteService.attachArticle" from the client.
export const attachArticlesToNote = createAsyncThunk(
	"breadcrumb/attachArticlesToNote",
	async ({note, articles, isUnassigned}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const { workspaceId } = note
		if (!workspaceId) {
			console.error("Failed to attach article to note: Invalid Workspace ID")
			return rejectWithValue("Failed to attach article to note. Invalid Workspace ID")
		}
		try {
			const { data: articleAttachmentDTO } = await axios.post(`/api/workspace/${workspaceId}/note/${note.id}/article`,
				{
					articles,
					isUnassigned,
					note
				}
			)
			const articleNoteRelationsList = articleAttachmentDTO.articleNote

			articleNoteRelationsList.forEach(({articleId}) => {
				// reload breadcrumbs if articleMap[articleId] !== null
				if (state.breadcrumbSlice.byWorkspace?.[workspaceId]?.articleMap?.[articleId]) {
					dispatch(fetchArticleBreadcrumbs({article: {id: articleId}, note, workspaceId}))
				} else {
					// don't update crumb but update unique count
					dispatch(fetchUniqueCounts({id: note.id, workspaceId}))
				}
			})
			
			return {articleNoteRelationsList, note, duplicateAttachmentCount: articleAttachmentDTO.duplicateAttachmentCount, workspaceId}
		} catch (error) {
			console.error("From breadcrumbs slice, failed to attach article to note", error)
			return rejectWithValue(error.response?.data || error.message)
		}
	}
)

export const deleteArticleNoteRelation = createAsyncThunk(
	"breadcrumb/deleteArticleNoteRelation",
	async ({note, articleId}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const {workspaceId} = note
		if (!workspaceId) {
			console.error("Unable to delete article note. Invalid Workspace ID")
			return rejectWithValue("Failed to remove note from article. Invalid Workspace ID")
		}
		try {
			// this route returns the note path (originally consumed by refresh relay)
			await axios.delete(`/api/workspace/${workspaceId}/note/${note.id}/article/${articleId}`)
			// reload breadcrumbs if articleMap[articleId] !== null
			if (state.breadcrumbSlice.byWorkspace[workspaceId].articleMap[articleId]) {
				dispatch(fetchArticleBreadcrumbs({article: {id: articleId}, note, workspaceId}))
			}
			return {
				note, 
				articleId,
				workspaceId
			}
		} catch (error) {
			console.error("From deleteArticleNoteRelation in BreadcrumbSlice. Failed to remove note from article: ", error)
			return rejectWithValue(error?.response?.message || `Failed to remove note ${note.id} from article ${articleId}`)
		}
	}
)

const initialState = {
	articleMap: {},
	trackedBreadcrumb: null,
	error: null
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const breadcrumbSlice = createSlice({
	name: "breadcrumb",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		clearBreadcrumbError: (state, action) => {
			const workspace = ensureWorkspaceTemplate(state, action.payload)
			workspace.error = initialState.error
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchArticleBreadcrumbs.fulfilled, (state, action) => {
			const {article, breadcrumbs, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			// we are ensuring there is only ever one breadcrumb present per-workspace.
			// without resetting the object, we could manage multiple breadcrumbs in memory which could 
			// be useful in the future
			workspace.trackedBreadcrumb = {
				articleId: article.id,
				breadcrumbTopLevelNoteIds: breadcrumbs.map(x => x.id)
			}
			workspace.articleMap[article.id] = breadcrumbs
		})

		builder.addCase(fetchArticleBreadcrumbs.rejected, (state, action) => {
			const {workspaceId} = action.meta.arg.note
			const {article} = action.meta.arg
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.error = action.payload || `Failed to load breadcrumbs for article ${article.id}`
		})
	}
})

export const { clearBreadcrumbError } = breadcrumbSlice.actions

export const selectArticleBreadcrumbs = (state, workspaceId) => state.breadcrumbSlice.byWorkspace[workspaceId]?.articleMap ?? initialState.articleMap

export default breadcrumbSlice.reducer